import React from "react";
import { IMAGES, SERVICES } from "../mock";
import { ButtonLink, Kicker, SectionHeading } from "../components/UI";
import { PageHero } from "./FindRenovate";
import { useReveal } from "../hooks/useReveal";
import { useSiteImages } from "../context/SiteImages";

const APPROACH = [
  "How you use your home", "What frustrates you about the current space", "How your family lives",
  "How you entertain", "What you want more of", "What you want less of",
  "Your aesthetic", "Your priorities", "Your budget", "Your long-term plans"
];

const SERVICE_KEYS = ["service_kitchen", "service_bath", "service_wholehome", "service_interior", "service_spaceplanning", "service_primarysuite"];

export default function DesignRenovation() {
  const ref = useReveal();
  const { simg } = useSiteImages();
  return (
    <div ref={ref}>
      <PageHero
        kicker="Design + Renovation"
        title={<>Love Where You <span className="text-gold-soft italic">Live Again.</span></>}
        sub="Thoughtful design and expertly managed renovations for homeowners ready to transform the space they already have."
        image={IMAGES.livingRoom2}
        imageKey="hero_design"
        cta={<ButtonLink to="/contact" variant="gold">Start My Renovation</ButtonLink>}
      />

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <SectionHeading kicker="Sometimes You Don't Need a New Home" title="You Need a New Vision for the Home You Have." center className="reveal" />
          <div className="reveal mt-10 space-y-4 font-serif text-2xl md:text-[1.8rem] text-navy/80 leading-relaxed font-light">
            <p>Maybe you've outgrown the layout. Maybe your kitchen doesn't work for your family. Maybe your bathrooms feel decades behind.</p>
            <p className="text-gold italic">Whatever isn't working, we help you rethink it.</p>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream-dark">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 img-zoom reveal">
            <img src={simg("design_approach", IMAGES.consultation)} alt="Design consultation" className="w-full h-[540px] object-cover" />
          </div>
          <div className="lg:col-span-6">
            <SectionHeading kicker="Our Approach" title="We Start With How You Want to Live." className="reveal" />
            <p className="reveal font-sans text-charcoal/70 leading-relaxed mt-6 font-light text-lg">Great design isn't simply about making a home beautiful. It's about making it work better. Before recommending materials, we want to understand:</p>
            <div className="reveal grid grid-cols-2 gap-x-6 gap-y-3 mt-7">
              {APPROACH.map((a) => (
                <div key={a} className="flex items-start gap-3"><span className="w-1.5 h-1.5 bg-gold rotate-45 shrink-0 mt-2" /><span className="font-sans text-sm text-charcoal/80">{a}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <SectionHeading kicker="Services" title="What We Design + Build" className="reveal mb-16" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((s, i) => (
              <div key={s.title} className="reveal group bg-white">
                <div className="img-zoom h-64"><img src={simg(SERVICE_KEYS[i], s.image)} alt={s.title} className="w-full h-full object-cover" /></div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl text-navy leading-tight">{s.title}</h3>
                  <p className="font-sans text-sm text-charcoal/65 leading-relaxed mt-3">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-navy text-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <SectionHeading dark kicker="Beautiful Is Only Part of the Goal" title={<>Functional. Comfortable.<br/>Personal. <span className="text-gold-soft italic">Timeless.</span></>} center className="reveal" />
          <p className="reveal font-sans text-cream/75 text-lg mt-8 font-light leading-relaxed">Every design decision should have a reason behind it. That's why we balance aesthetics with usability, durability, budget, and the way your family actually lives.</p>
          <div className="reveal flex justify-center mt-10"><ButtonLink to="/contact" variant="gold">Schedule a Design Consultation</ButtonLink></div>
        </div>
      </section>
    </div>
  );
}
